import { i as ViewPort } from "./enums-ArxA_qeI.js";
//#region src/utils/utils.ts
var uuid = () => crypto.randomUUID();
var normalize = (value, min, max) => {
	if (min === max) return 1;
	return (value - min) / (max - min);
};
var denormalize = (normalized, min, max) => {
	return (max - min) * normalized + min;
};
var getRandom = (min, max) => {
	return Math.random() * (max - min) + min;
};
var intersects = (start1X, start1Y, end1X, end1Y, start2X, start2Y, end2X, end2Y) => {
	if (!(start2X > end1X || end2X < start1X || start2Y > end1Y || end2Y < start1Y)) if (!(start2X < start1X || start2Y < start1Y || end2X > end1X || end2Y > end1Y)) return ViewPort.INSIDE;
	else return ViewPort.INTERSECT;
	else return ViewPort.OUTSIDE;
};
var clampMin = (value, min) => Math.max(value, min);
var clampMax = (value, max) => Math.min(value, max);
var clamp = (value, min, max) => Math.min(Math.max(value, min), max);
var lerp = (a, b, t) => (1 - t) * a + t * b;
var canConnect = (source, destination, rules, executionGraph) => {
	if (!destination) return false;
	if (source === destination) return false;
	if (source.node === destination.node) return false;
	if (source.type === destination.type) return false;
	if (!rules[source.dataType].includes(destination.dataType)) return false;
	if (!executionGraph.canConnect(source.node, destination.node)) return false;
	return true;
};
var isEmpty = (obj) => {
	for (let _ in obj) return false;
	return true;
};
var isInRange = (value, min, max) => {
	return Math.min(value, min) >= min && Math.max(value, max) <= max;
};
var exists = (value) => typeof value !== "undefined" && value !== null;
var binarySearch = ({ max, getValue, match }) => {
	let min = 0;
	while (min <= max) {
		let mid = Math.floor((min + max) / 2);
		const curr = getValue(mid);
		if (curr === match) return mid;
		if (curr < match) min = mid + 1;
		else max = mid - 1;
	}
	return max;
};
var noop = () => {};
var capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);
var cloneAudioBuffer = (from) => {
	const cloned = new AudioBuffer({
		length: from.length,
		numberOfChannels: from.numberOfChannels,
		sampleRate: from.sampleRate
	});
	for (let i = 0; i < cloned.numberOfChannels; ++i) cloned.copyToChannel(from.getChannelData(i), i);
	return cloned;
};
var isVector = (obj) => {
	return typeof obj === "object" && obj && Object.keys(obj).every((k) => ["x", "y"].includes(k) && typeof obj[k] === "number");
};
//#endregion
//#region src/utils/logger.ts
var LogLevel = /* @__PURE__ */ function(LogLevel) {
	LogLevel[LogLevel["TRACE"] = 1] = "TRACE";
	LogLevel[LogLevel["DEBUG"] = 2] = "DEBUG";
	LogLevel[LogLevel["WARN"] = 3] = "WARN";
	LogLevel[LogLevel["INFO"] = 4] = "INFO";
	LogLevel[LogLevel["LOG"] = 5] = "LOG";
	LogLevel[LogLevel["ERROR"] = 6] = "ERROR";
	LogLevel[LogLevel["DISABLED"] = 7] = "DISABLED";
	return LogLevel;
}({});
var Log = class {
	static {
		this.LOG_LEVEL = 4;
	}
	static trace(...args) {
		if (this.LOG_LEVEL > 1) return;
		console.trace.apply(this, args);
	}
	static debug(...args) {
		if (this.LOG_LEVEL > 2) return;
		console.debug.apply(this, args);
	}
	static warn(...args) {
		if (this.LOG_LEVEL > 3) return;
		console.warn.apply(this, args);
	}
	static info(...args) {
		if (this.LOG_LEVEL > 4) return;
		console.info.apply(this, args);
	}
	static log(...args) {
		if (this.LOG_LEVEL > 5) return;
		console.log.apply(this, args);
	}
	static error(...args) {
		if (this.LOG_LEVEL > 6) return;
		console.error.apply(this, args);
	}
};
//#endregion
//#region src/core/hooks.ts
/**
* Hooks is a general purpose class used by FlowConnect, Flow, Node, UINode, Terminal, Group... classes
* for making their normal processes available as events to listen to when they happen.
* For e.g you can listen to any Terminal's 'connect'/'disconnect' event to do some custom stuff
*/
var Hooks = class {
	constructor() {
		this.registeredEvents = {};
		this.lastId = 0;
	}
	/**
	* Register a callback to an event
	*
	* @param eventKey name of the event to listen to
	* @param callback reference to a callback that will be triggered when this event happens
	* @returns a numbered ID that can be used later to de-register this hook
	*/
	on(eventKey, callback) {
		if (!this.registeredEvents[eventKey]) this.registeredEvents[eventKey] = {};
		let id = this.lastId;
		this.registeredEvents[eventKey][id] = callback;
		this.lastId += 1;
		return id;
	}
	call(eventKey, ...args) {
		if (this.registeredEvents[eventKey]) {
			if (!args) args = [];
			Object.values(this.registeredEvents[eventKey]).forEach((callback) => callback(...args));
		}
	}
	/**
	* De-register a callback to an event
	*
	* @param eventKey name of the event to de-register
	* @param id a numbered id that was generated at the time of event registration
	*/
	off(eventKey, id) {
		if (this.registeredEvents[eventKey]) delete this.registeredEvents[eventKey][id];
	}
	offAll() {
		this.registeredEvents = {};
	}
};
//#endregion
//#region src/resource/constants.ts
var Constant = class {
	static {
		this.TAU = 2 * Math.PI;
	}
	static {
		this.SIN_60 = .866;
	}
	static {
		this.PHI = 1.618033988749895;
	}
};
//#endregion
//#region src/utils/avl-tree.ts
var AVLTree = class {
	constructor(comparator, dataToTreeNodeMapper) {
		this.comparator = comparator;
		this.dataToTreeNodeMapper = dataToTreeNodeMapper;
		this.root = void 0;
		this.dataRefToTreeNode = {};
		this.size = 0;
	}
	height(node) {
		if (!node) return 0;
		return node.height;
	}
	rightRotate(node) {
		let newRoot = node.left;
		node.left = newRoot?.right;
		if (newRoot) newRoot.right = node;
		node.height = Math.max(node.left ? node.left.height : 0, node.right ? node.right.height : 0) + 1;
		if (newRoot) newRoot.height = Math.max(newRoot.left ? newRoot.left.height : 0, newRoot.right ? newRoot.right.height : 0) + 1;
		return newRoot;
	}
	leftRotate(node) {
		let newRoot = node.right;
		node.right = newRoot?.left;
		if (newRoot) newRoot.left = node;
		node.height = Math.max(node.left ? node.left.height : 0, node.right ? node.right.height : 0) + 1;
		if (newRoot) newRoot.height = Math.max(newRoot.left ? newRoot.left.height : 0, newRoot.right ? newRoot.right.height : 0) + 1;
		return newRoot;
	}
	getBalance(node) {
		if (!node) return 0;
		return (node.left ? node.left.height : 0) - (node.right ? node.right.height : 0);
	}
	add(data) {
		let res = { node: void 0 };
		if (this.root) this.root = this._insert(this.root, data, res);
		this.dataRefToTreeNode[this.dataToTreeNodeMapper(data)] = res.node;
		this.size += 1;
		return res.node;
	}
	_insert(node, data, res) {
		if (!node) {
			let newNode = new AVLTreeNode([data]);
			res.node = newNode;
			return newNode;
		}
		if (this.comparator(data, node.data[0]) === 0) {
			node.data.push(data);
			res.node = node;
			return node;
		}
		if (this.comparator(data, node.data[0]) < 0) node.left = this._insert(node.left, data, res);
		else node.right = this._insert(node.right, data, res);
		node.height = 1 + Math.max(node.left ? node.left.height : 0, node.right ? node.right.height : 0);
		let balance = this.getBalance(node);
		if (balance > 1 && node.left && this.comparator(data, node.left.data[0]) < 0) return this.rightRotate(node);
		if (balance > 1 && node.left && this.comparator(data, node.left.data[0]) > 0) {
			node.left = this.leftRotate(node.left);
			return this.rightRotate(node);
		}
		if (balance < -1 && node.right && this.comparator(data, node.right.data[0]) > 0) return this.leftRotate(node);
		if (balance < -1 && node.right && this.comparator(data, node.right.data[0]) < 0) {
			node.right = this.rightRotate(node.right);
			return this.leftRotate(node);
		}
		return node;
	}
	remove(data) {
		let res = { deleted: false };
		this.root = this._delete(this.root, data, res);
		delete this.dataRefToTreeNode[this.dataToTreeNodeMapper(data)];
		this.size -= 1;
		return res.deleted;
	}
	_delete(node, data, res) {
		if (!node) return node;
		if (this.comparator(data, node.data[0]) < 0) node.left = this._delete(node.left, data, res);
		else if (this.comparator(data, node.data[0]) > 0) node.right = this._delete(node.right, data, res);
		else if (node.data.length > 1) {
			let index = node.data.findIndex((currData) => this.dataToTreeNodeMapper(currData) === this.dataToTreeNodeMapper(data));
			if (index > -1) {
				node.data.splice(index, 1);
				res.deleted = true;
			} else res.deleted = false;
			return node;
		} else if (!node.left || !node.right) {
			let temp = void 0;
			if (!node.left) temp = node.right;
			else temp = node.left;
			if (!temp) node = void 0;
			else node = temp;
			res.deleted = true;
		} else {
			let temp = this.minValue(node.right);
			node.data = temp.data;
			node.right = this._delete(node.right, temp.data[0], res);
			res.deleted = true;
		}
		if (!node) return node;
		node.height = Math.max(node.left ? node.left.height : 0, node.right ? node.right.height : 0) + 1;
		let balance = this.getBalance(node);
		if (balance > 1 && node.left && this.getBalance(node.left) >= 0) return this.rightRotate(node);
		if (balance > 1 && node.left && this.getBalance(node.left) < 0) {
			node.left = this.leftRotate(node.left);
			return this.rightRotate(node);
		}
		if (balance < -1 && node.right && this.getBalance(node.right) <= 0) return this.leftRotate(node);
		if (balance < -1 && node.right && this.getBalance(node.right) > 0) {
			node.right = this.rightRotate(node.right);
			return this.leftRotate(node);
		}
		return node;
	}
	minValue(node) {
		let curr = node;
		while (curr.left) curr = curr.left;
		return curr;
	}
	forEach(action) {
		this._inorder(this.root, action);
	}
	_inorder(node, action) {
		if (node) {
			this._inorder(node.left, action);
			node.data.forEach((data) => action(data));
			this._inorder(node.right, action);
		}
	}
	_reverseInorder(node, action) {
		if (node) {
			this._inorder(node.right, action);
			node.data.forEach((data) => action(data));
			this._inorder(node.left, action);
		}
	}
};
var AVLTreeNode = class {
	constructor(data) {
		this.data = data;
		this.height = 1;
		this.left = void 0;
		this.right = void 0;
	}
};
//#endregion
//#region src/utils/linked-list.ts
var List = class extends Hooks {
	constructor(comparator, source) {
		super();
		this.comparator = comparator;
		this.length = 0;
		if (source) source.forEach((value) => this.append(value));
	}
	prepend(data) {
		let newNode = new ListNode(data);
		if (!this.head) {
			this.head = newNode;
			this.tail = newNode;
		} else {
			this.head.prev = newNode;
			newNode.next = this.head;
			this.head = newNode;
		}
		this.length += 1;
		this.call("prepend", this, newNode);
		return newNode;
	}
	append(data) {
		let newNode = data instanceof ListNode ? data : new ListNode(data);
		if (!this.tail) {
			this.head = newNode;
			this.tail = newNode;
		} else {
			this.tail.next = newNode;
			newNode.prev = this.tail;
			this.tail = newNode;
		}
		this.length += 1;
		this.call("append", this, newNode);
		return newNode;
	}
	removeFirst(hook = true) {
		let removed;
		if (!this.head) return;
		else if (this.length === 1) {
			removed = this.head.data;
			this.head = void 0;
			this.tail = void 0;
		} else {
			removed = this.head.data;
			if (this.head.next) this.head.next.prev = void 0;
			this.head = this.head.next;
		}
		this.length -= 1;
		if (hook) this.call("removefirst", this, removed);
		return removed;
	}
	removeLast() {
		let removed;
		if (!this.tail) return;
		else if (this.length === 1) {
			removed = this.tail.data;
			this.head = void 0;
			this.tail = void 0;
		} else {
			removed = this.tail.data;
			if (this.tail.prev) this.tail.prev.next = void 0;
			this.tail = this.tail.prev;
		}
		this.length -= 1;
		this.call("removelast", this, removed);
		return removed;
	}
	addAfter(data, node) {
		let newNode = new ListNode(data);
		newNode.next = node.next;
		newNode.prev = node;
		node.next = newNode;
		if (newNode.next) newNode.next.prev = newNode;
		if (node === this.tail) this.tail = newNode;
		this.length += 1;
		return newNode;
	}
	addBefore(data, node) {
		let newNode = new ListNode(data);
		newNode.prev = node.prev;
		newNode.next = node;
		node.prev = newNode;
		if (newNode.prev) newNode.prev.next = newNode;
		if (node === this.head) this.head = newNode;
		this.length += 1;
		return newNode;
	}
	delete(node) {
		if (this.length === 1) {
			this.head = void 0;
			this.tail = void 0;
		} else if (node === this.head) this.removeFirst();
		else if (node === this.tail) this.removeLast();
		else {
			if (node.prev) node.prev.next = node.next;
			if (node.next) node.next.prev = node.prev;
			this.length -= 1;
		}
	}
	searchHead(callback) {
		let curr = this.head;
		while (curr) {
			if (callback(curr)) return curr;
			curr = curr.next;
		}
	}
	searchTail(callback) {
		let curr = this.tail;
		while (curr) {
			if (callback(curr)) return curr;
			curr = curr.prev;
		}
	}
	get(index) {
		if (index >= this.length) return;
		else if (index === 0) return this.head ? this.head.data : void 0;
		else if (index === this.length - 1) return this.tail ? this.tail.data : void 0;
		else {
			let count = 0;
			let curr = this.head;
			while (curr) {
				if (count === index) return curr.data;
				count += 1;
				curr = curr.next;
			}
			return;
		}
	}
	forEach(callback) {
		let curr = this.head;
		while (curr) {
			callback(curr);
			curr = curr.next;
		}
	}
	map(callback) {
		let mapped = [];
		this.forEach((node) => mapped.push(callback(node)));
		return mapped;
	}
	toArray() {
		let data = [];
		this.forEach((node) => node.data && data.push(node.data));
		return data;
	}
	toString() {
		return this.toArray().join(", ");
	}
};
var ListNode = class {
	constructor(data) {
		this.prev = void 0;
		this.next = void 0;
		this.data = data ? data : void 0;
	}
};
//#endregion
//#region src/utils/bidirectional-map.ts
var BiMap = class {
	constructor() {
		this.map = /* @__PURE__ */ new Map();
		this.reverseMap = /* @__PURE__ */ new Map();
	}
	get(keyOrVal) {
		return this.map.get(keyOrVal) || this.reverseMap.get(keyOrVal);
	}
	set(key, value) {
		this.map.set(key, value);
		this.reverseMap.set(value, key);
	}
	has(keyOrVal) {
		return this.map.has(keyOrVal) || this.reverseMap.has(keyOrVal);
	}
	delete(keyOrVal) {
		let temp1 = this.map.get(keyOrVal);
		let temp2 = this.reverseMap.get(keyOrVal);
		if (typeof temp1 !== "undefined") this.map.delete(keyOrVal);
		if (typeof temp2 !== "undefined") this.reverseMap.delete(keyOrVal);
		if (temp1) {
			this.map.delete(temp1);
			this.reverseMap.delete(temp1);
		}
		if (temp2) {
			this.map.delete(temp2);
			this.reverseMap.delete(temp2);
		}
	}
	clear() {
		this.map.clear();
		this.reverseMap.clear();
	}
};
//#endregion
//#region src/utils/lexer.ts
var Lexer = class Lexer {
	static {
		this.operators = [
			"+",
			"-",
			"*",
			"/",
			"^",
			"%",
			"&",
			"|"
		];
	}
	constructor() {
		this.result = [];
		this.numberBuffer = [];
		this.letterBuffer = [];
	}
	tokenize(expr) {
		this.result = [];
		this.numberBuffer = [];
		this.letterBuffer = [];
		let index;
		while ((index = expr.indexOf("...")) !== -1) expr = expr.substring(0, index) + expr.charAt(index + 3).toUpperCase() + expr.substring(index + 4);
		expr = this.replaceConstants(expr);
		expr.replace(/\s+/g, "").split("").forEach((char) => {
			if (this.isDigit(char)) this.numberBuffer.push(char);
			else if (this.isDecimalPoint(char)) this.numberBuffer.push(char);
			else if (this.isLetter(char)) {
				if (this.numberBuffer.length) {
					this.processNumberBuffer();
					this.result.push(new Token("Operator", "*"));
				}
				this.letterBuffer.push(char);
			} else if (this.isOperator(char)) {
				if (this.numberBuffer.length) this.processNumberBuffer();
				else if (this.letterBuffer.length) this.processLetterBuffer();
				this.result.push(new Token("Operator", char));
			} else if (this.isLeftParenthesis(char)) {
				if (this.letterBuffer.length) {
					this.result.push(new Token("Function", this.letterBuffer.join("")));
					this.letterBuffer = [];
				} else if (this.numberBuffer.length) {
					this.processNumberBuffer();
					this.result.push(new Token("Operator", "*"));
				}
				this.result.push(new Token("LParenthesis", char));
			} else if (this.isRightParenthesis(char)) {
				if (this.letterBuffer.length) this.processLetterBuffer();
				if (this.numberBuffer.length) this.processNumberBuffer();
				this.result.push(new Token("RParenthesis", char));
			} else if (this.isComma(char)) {
				if (this.numberBuffer.length) this.processNumberBuffer();
				if (this.letterBuffer.length) this.processLetterBuffer();
				this.result.push(new Token("ArgSeperator", char));
			} else throw Error("Unknown character: " + char);
		});
		if (this.numberBuffer.length) this.processNumberBuffer();
		if (this.letterBuffer.length) this.processLetterBuffer();
		return this.result;
	}
	replaceConstants(expr) {
		return expr.replace(/pi/g, Evaluator.constants.pi.toString()).replace(/tau/g, Evaluator.constants.tau.toString()).replace(/phi/g, Evaluator.constants.phi.toString()).replace(/ln2/g, Evaluator.constants.ln2.toString()).replace(/ln10/g, Evaluator.constants.ln10.toString()).replace(/log2e/g, Evaluator.constants.log2e.toString()).replace(/log10e/g, Evaluator.constants.log10e.toString());
	}
	processNumberBuffer() {
		let literal = parseFloat(this.numberBuffer.join(""));
		this.numberBuffer = [];
		if (!isNaN(literal)) this.result.push(new Token("Literal", literal));
	}
	processLetterBuffer() {
		for (let i = 0; i < this.letterBuffer.length; i++) {
			this.result.push(new Token("Variable", this.letterBuffer[i]));
			if (i < this.letterBuffer.length - 1) this.result.push(new Token("Operator", "*"));
		}
		this.letterBuffer = [];
	}
	isComma(char) {
		return char === ",";
	}
	isDigit(char) {
		return /\d/.test(char);
	}
	isLetter(char) {
		return /[a-zA-Z]/.test(char);
	}
	isOperator(char) {
		return Lexer.operators.includes(char);
	}
	isLeftParenthesis(char) {
		return char === "(";
	}
	isRightParenthesis(char) {
		return char == ")";
	}
	isDecimalPoint(char) {
		return char === ".";
	}
};
var TokenType = /* @__PURE__ */ function(TokenType) {
	TokenType["Literal"] = "Literal";
	TokenType["Variable"] = "Variable";
	TokenType["Operator"] = "Operator";
	TokenType["LParenthesis"] = "LParenthesis";
	TokenType["RParenthesis"] = "RParenthesis";
	TokenType["Function"] = "Function";
	TokenType["ArgSeperator"] = "ArgSeperator";
	return TokenType;
}({});
var Token = class Token {
	static {
		this.associativity = {
			"^": "right",
			"*": "left",
			"/": "left",
			"+": "left",
			"-": "left",
			"%": "left",
			"&": "left",
			"|": "left"
		};
	}
	static {
		this.precedence = {
			"|": 0,
			"&": 1,
			"+": 2,
			"-": 2,
			"*": 3,
			"/": 3,
			"%": 3,
			"^": 4
		};
	}
	get precedence() {
		return Token.precedence[this.value];
	}
	get associativity() {
		return Token.associativity[this.value];
	}
	constructor(type, value) {
		this.type = type;
		this.value = value;
		this.count = 0;
	}
	toString() {
		return this.type + "(" + this.value + ")";
	}
};
//#endregion
//#region src/utils/parser.ts
var Parser = class {
	constructor() {
		this.lexer = new Lexer();
	}
	peek(arr) {
		return arr.slice(-1)[0];
	}
	parse(expression) {
		let tokens = this.lexer.tokenize(expression);
		let stack = [];
		let output = [];
		let were = [];
		let argCount = [];
		tokens.forEach((token) => {
			if (token.type === TokenType.Literal || token.type === TokenType.Variable) {
				output.push(token);
				if (typeof this.peek(were) !== "undefined") {
					were.pop();
					were.push(true);
				}
			} else if (token.type === TokenType.Function) {
				stack.push(token);
				argCount.push(0);
				if (typeof this.peek(were) !== "undefined") {
					were.pop();
					were.push(true);
				}
				were.push(false);
			} else if (token.type === TokenType.ArgSeperator) {
				while (this.peek(stack) && this.peek(stack).type !== TokenType.LParenthesis) output.push(stack.pop());
				if (were.pop()) argCount.push(argCount.pop() + 1);
				were.push(false);
			} else if (token.type == TokenType.Operator) {
				while (this.peek(stack) && this.peek(stack).type === TokenType.Operator && (token.associativity === "left" && token.precedence <= this.peek(stack).precedence || token.associativity === "right" && token.precedence < this.peek(stack).precedence)) output.push(stack.pop());
				stack.push(token);
			} else if (token.type === TokenType.LParenthesis) stack.push(token);
			else if (token.type === TokenType.RParenthesis) {
				while (this.peek(stack) && this.peek(stack).type !== TokenType.LParenthesis) output.push(stack.pop());
				stack.pop();
				if (this.peek(stack) && this.peek(stack).type === TokenType.Function) {
					let funcToken = stack.pop();
					let a = argCount.pop();
					if (were.pop()) a += 1;
					funcToken.count = a;
					output.push(funcToken);
				}
			}
		});
		while (this.peek(stack)) output.push(stack.pop());
		return output;
	}
};
//#endregion
//#region src/utils/evaluator.ts
var Evaluator = class Evaluator {
	static {
		this.constants = {
			tau: Constant.TAU,
			pi: Math.PI,
			phi: Constant.PHI,
			ln2: Math.LN2,
			ln10: Math.LN10,
			log2e: Math.LOG2E,
			log10e: Math.LOG10E
		};
	}
	static {
		this.functions = {
			cos: Math.cos,
			acos: Math.acos,
			acosh: Math.acosh,
			sin: Math.sin,
			asin: Math.asin,
			asinh: Math.asinh,
			tan: Math.tan,
			atan: Math.atan,
			atanh: Math.atanh,
			atan2: Math.atan2,
			abs: Math.abs,
			ceil: Math.ceil,
			round: Math.round,
			floor: Math.floor,
			exp: Math.exp,
			random: Math.random,
			sqrt: Math.sqrt,
			cbrt: Math.cbrt,
			log10: Math.log10,
			log2: Math.log2,
			log: (value, base) => {
				return base ? Math.log(value) / Math.log(base) : Math.log(value);
			},
			dist: (x1, y1, x2, y2) => {
				return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
			},
			gcd: (a, b) => {
				while (b !== 0) {
					const r = a % b;
					a = b;
					b = r;
				}
				return a < 0 ? -a : a;
			},
			max: (...args) => {
				return Array.isArray(args[0]) ? Math.max(...args[0]) : Math.max(...args);
			},
			min: (...args) => {
				return Array.isArray(args[0]) ? Math.min(...args[0]) : Math.min(...args);
			},
			hypot: (...args) => {
				let result = 0;
				let largest = 0;
				(Array.isArray(args[0]) ? args[0] : args).forEach((x) => {
					const value = Math.abs(x);
					if (largest < value) {
						result = result * (largest / value) * (largest / value);
						result += 1;
						largest = value;
					} else result = result + (value > 0 ? value / largest * (value / largest) : value);
				});
				return largest * Math.sqrt(result);
			},
			lcm: (a, b) => {
				if (a === 0) return a;
				if (b === 0) return b;
				const prod = a * b;
				while (b !== 0) {
					const t = b;
					b = a % t;
					a = t;
				}
				return Math.abs(prod / a);
			},
			sum: (...args) => {
				return (Array.isArray(args[0]) ? args[0] : args).reduce((acc, curr) => acc + curr, 0);
			},
			mean: (...args) => {
				let input = Array.isArray(args[0]) ? args[0] : args;
				return Evaluator.functions.sum(input) / input.length;
			},
			median: (...args) => {
				let input = Array.isArray(args[0]) ? args[0] : args;
				input.sort((a, b) => a - b);
				return input.length % 2 === 0 ? (input[input.length / 2 - 1] + input[input.length / 2]) / 2 : input[Math.floor(input.length / 2)];
			},
			std: (...args) => {
				let input = Array.isArray(args[0]) ? args[0] : args;
				return Math.sqrt(Evaluator.functions.var(input));
			},
			var: (...args) => {
				let input = Array.isArray(args[0]) ? args[0] : args;
				let mean = Evaluator.functions.mean(input);
				return input.reduce((acc, curr) => acc + Math.pow(curr - mean, 2), 0) / input.length;
			},
			fact: (n) => {
				let res = 1;
				for (let i = n; i > 1; i -= 1) res *= i;
				return res;
			}
		};
	}
	static {
		this.multiargFunctions = [
			"max",
			"min",
			"hypot",
			"sum",
			"mean",
			"median",
			"std",
			"var"
		];
	}
	constructor(variables) {
		this.variables = variables;
		this.history = {};
		this.orderedExprs = [];
		this.historyLimit = 3;
		this.parser = new Parser();
	}
	evaluate(expr, index) {
		let postFix;
		if (this.history[expr]) postFix = this.history[expr];
		else {
			if (this.orderedExprs.length === this.historyLimit) {
				let oldestExpr = this.orderedExprs.shift();
				delete this.history[oldestExpr];
			}
			this.orderedExprs.push(expr);
			this.history[expr] = this.parser.parse(expr);
			postFix = this.history[expr];
		}
		let result = [];
		for (let token of postFix) switch (token.type) {
			case TokenType.Literal:
				result.push(token.value);
				break;
			case TokenType.Variable:
				if (token.value !== token.value.toLowerCase()) if (!Array.isArray(this.variables[token.value])) throw new Error("Spread operator only allowed for array variables: " + token.value.toLowerCase());
				else result.push(this.variables[token.value]);
				else if (Array.isArray(this.variables[token.value]) && typeof index !== "undefined") result.push(this.variables[token.value][index]);
				else result.push(this.variables[token.value]);
				break;
			case TokenType.Operator: {
				let [left, right] = [result.pop(), result.pop()];
				if (typeof left !== "number" || typeof right !== "number") throw new Error("Only numbers are allowed for operators");
				switch (token.value) {
					case "+":
						result.push(left + right);
						break;
					case "*":
						result.push(left * right);
						break;
					case "-":
						result.push(right - left);
						break;
					case "/":
						result.push(right / left);
						break;
					case "%":
						result.push(right % left);
						break;
					case "^":
						result.push(Math.pow(right, left));
						break;
					case "&":
						result.push(right & left);
						break;
					case "|":
						result.push(right | left);
						break;
					default: throw new Error("Unknow operator: " + token.value);
				}
				break;
			}
			case TokenType.Function: {
				let args = [];
				for (let i = 0; i < token.count; i += 1) args.push(result.pop());
				result.push(Evaluator.functions[token.value](...args));
				break;
			}
			default: throw new Error("Unknown token type: " + token.type);
		}
		return result[0];
	}
};
//#endregion
export { noop as A, exists as C, isInRange as D, isEmpty as E, uuid as M, isVector as O, denormalize as S, intersects as T, capitalize as _, TokenType as a, clampMin as b, ListNode as c, Constant as d, Hooks as f, canConnect as g, binarySearch as h, Token as i, normalize as j, lerp as k, AVLTree as l, LogLevel as m, Parser as n, BiMap as o, Log as p, Lexer as r, List as s, Evaluator as t, AVLTreeNode as u, clamp as v, getRandom as w, cloneAudioBuffer as x, clampMax as y };

//# sourceMappingURL=utils-DpUdZbGl.js.map